import { Component, OnChanges, OnDestroy, HostBinding } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { ViewRenderer } from '@unpispas/upp-wdgt';

import { languageService } from '@unpispas/upp-base';
import { i18nSupport } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';

import { AppConstants } from '@unpispas/upp-defs';
import { PriceType, PriceInfo } from '@unpispas/upp-defs';

import { ProductView } from '@unpispas/upp-data';
import { PreselectView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

export type ProductThumbTargets = 'CREATE' | 'DIRECT' | 'SELECT' | 'TICKET' | 'PRDNO' | 'SORT' | 'OTHER';

@Component({
    selector: 'upp-product-thumb',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-thumb.html',
    styleUrls: [
      './upp-thumb.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]       
})
export class UppProductThumbComponent extends i18nSupport implements OnChanges, OnDestroy {
    @Input() product: ProductView | null = null;
    @Input() direct: PreselectView | null = null;
    @Input() ticket: TicketView | null = null;
    @Input() target: ProductThumbTargets | null = null;
    @Input() showall = true;
    @Input() checked = false;

    @Output() Select = new EventEmitter <ProductView | PreselectView | null> ();
    @Output() Checked = new EventEmitter <ProductView | PreselectView | null> ();
    @Output() Modify = new EventEmitter <ProductView | PreselectView | null> ();

    private _direct_subscription: Subscription | null = null;
    private _product_subscription: Subscription | null = null;
    private _ticket_subscription: Subscription | null = null;

    constructor(private lang: languageService, private change: ViewRenderer, private data: dataService, public view: viewService){
        super(lang, change.cdref);
    }

    ngOnChanges(){
        if (this._direct_subscription){
            this._direct_subscription.unsubscribe();
            this._direct_subscription = null;
        }

        if (this._product_subscription){
            this._product_subscription.unsubscribe();
            this._product_subscription = null;
        }

        if (this._ticket_subscription){
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }

        if (this.direct){
            this._direct_subscription = this.direct.OnViewRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }

        if (this.product){
            this._product_subscription = this.product.OnViewRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }

        if (this.ticket){
            this._ticket_subscription = this.ticket.OnViewRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._direct_subscription){
            this._direct_subscription.unsubscribe();
            this._direct_subscription = null;
        }

        if (this._product_subscription){
            this._product_subscription.unsubscribe();
            this._product_subscription = null;
        }

        if (this._ticket_subscription){
            this._ticket_subscription.unsubscribe();
            this._ticket_subscription = null;
        }
    }

    /********************************/
    /* DISPLAY OPTIONS              */
    /********************************/    

    get IsDirectProduct() {
        return (this.target == 'DIRECT') && (this.direct == null);
    }

    @HostBinding('class.unavailable')
    get TicketDimmed(): boolean {
        if (this.target !== 'TICKET' && this.target !== 'DIRECT'){
            return false;
        }

        if (this.direct){
            return !this.direct.IsAvailable;
        }

        if (this.product){
            return !this.product.IsAvailable;
        }

        return false;
    }

    get thumbname(){
        if (this.direct){
            return this.tr(this.direct.name);
        }

        if (this.product){
            return this.tr(this.product.name);
        }

        return 'null';
    }

    get thumbtype(){
        if (this.direct){
            return 'direct';
        }

        if (this.product){
            if (this.product.IsProduct){
                return 'product'
            }

            if (this.product.IsGeneric){
                return 'generic'
            }

            if (this.product.IsGroup){
                return 'group'
            }

            if (this.product.IsFamily){
                return 'family'
            }
        }

        return 'null';
    }

    /********************************/
    /* PRICE DISPLAY                */
    /********************************/

    /**
     * Chip price for the current thumb item: shortcut (`direct`) charge, bare product
     * as "options" (`target === 'DIRECT'`), or catalog minimum (`MinPrice`) otherwise.
     * Uses the ticket's place/table when present.
     */
    get ThumbPrice(): PriceType | null {
        const _place = this.ticket?.place ?? null;
        const _qrcode = this.ticket?.qrcode ?? null;

        if (this.direct){
            if (this.direct.IsMarket){
                return null;
            }

            if (this.ticket){
                const _p = this.direct.tableprice(_place, _qrcode);
                const _r = this.direct.product?.taxrate ?? AppConstants.defaultTaxRate;
                return new PriceInfo(_p, _r).price;
            }

            return this.direct.priceinfo;
        }

        if (!this.product){
            return null;
        }

        // catalog product card: "from" minimum configuration
        if (this.target !== 'DIRECT'){
            return this.product.MinPrice(_place, _qrcode);
        }

        // product shown as the "options" direct slot: base product price only
        if (!this.product.IsProduct || this.product.IsMarket){
            return null;
        }

        if (this.ticket){
            const _p = this.product.tableprice(_place, _qrcode);
            const _r = this.product.taxrate ?? AppConstants.defaultTaxRate;
            return new PriceInfo(_p, _r).price;
        }

        return this.product.priceinfo;
    }

    /********************************/
    /* INCART CALCULATIONS          */
    /********************************/    

    private _DirectInCart(direct: PreselectView): number {
        let _incart = 0;

        if (!this.ticket) {
            return 0;
        }

        for(const _ticketproduct of this.ticket.products){
            if (_ticketproduct.IsDirect(direct)){
                _incart += _ticketproduct.amount || 0;
            }
        }

        return _incart;        
    }

    private _ProductInCart(product: ProductView): number {
        let _incart = 0;

        if (!this.ticket) {
            return 0;
        }

        for(const _ticketproduct of this.ticket.products){
            if (_ticketproduct.IsProduct(product)){
                _incart += _ticketproduct.amount || 0;
            }
        }

        // considerations for product in the directs view
        if (this.IsDirectProduct){  
            if ((this.product != null) && (this.direct == null)){
                for (const _preselect of this.product.selects){
                    _incart -= this._DirectInCart(_preselect);
                }
            }
        }

        return _incart;        
    }

    private _GroupInCart(group: ProductView): number {
        let _incart = 0;

        if (!this.ticket){
            return 0;
        }        

        for(const product of group.products){
            if (product.IsGroup){   // recursive call inside the group
                _incart += this._GroupInCart(product);
            }
            else {  // product or generic: check if included in ticket
                _incart += this._ProductInCart(product);
            }
        }
        
        return _incart;
    }

    private get _InCart(): number {
        if (!this.ticket){
            return 0;
        }

        if (this.direct){
            return this._DirectInCart(this.direct);
        }

        if (this.product){
            if (this.product.IsGroup){
                return this._GroupInCart(this.product);
            }

            if (this.product.IsProduct){
                return this._ProductInCart(this.product);
            }
            
            if (this.product.IsGeneric){
                return this._ProductInCart(this.product);
            }
        }

        return 0;

    }

    private _incart: number | null = null;
    get InCart(): number {
        if (!this.ticket){
            return 0;
        }

        if (this._incart === null){
            this._incart = this._InCart;

            // keep the value during this event loop
            Promise.resolve().then(() => {
                this._incart = null;    
            });    
        }

        return this._incart;
    }

    /********************************/
    /* EVENT HANDLERS               */
    /********************************/

    OnSelect(){
        this.Select.emit(this.direct || this.product);
    }

    OnModify(){
        this.Modify.emit(this.direct || this.product);
    }

    /**
     * Long-press on the product thumb.
     * TODO: open the ticket product keyboard in an embedded modal so the
     * staff can set how this item is added to the cart (amount / weight).
     */
    OnPressed(value: boolean){
        console.log("TODO: pasa por aqui: OnPressed", value);   // TODO
    }

    OnCheck(){
        this.Checked.emit(this.direct || this.product);        
    }
}